/*
 * EntityCreate3.tsx
 *
 * Created by Dr. Maximillian Dornseif 2023-10-06 in huWaWi3 33.1.0
 * Copyright (c) 2023 HUDORA GmbH
 */

import { useSchema , LadeChecker } from '@hudora/hd-react-components';
import { AlertTitle } from '@mui/material';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { assertIsObject } from 'assertate-debug';
import { DocumentNode } from 'graphql';
import { cleanGqlInput } from 'graphql-clean-diff';
import { JSONSchema7 } from 'json-schema';
import React, { useCallback, useState } from 'react';
import ReactDiffViewer from 'react-diff-viewer';
import ReactJson from 'react-json-view';
import { useMutation } from 'urql';

import { IEntity } from '../../../types';
import { getErrorMessageBar } from '../UiChecker';
import { DisplayUserInfo } from '../widgets/DisplayUserInfo';
import { HdSpinner } from '../widgets/HdSpinner';
import { prepareForGqlPatch } from './EntityMutate3';
import { EntityMutateForm3 } from './EntityMutateForm3';

/** Neue Entity anlegen.
 *
 * Dafür füllen wir das ganze vorher mit einem Template aus.
 * */

// Entity in einem Panel Anlegen/Bearbeiten
export interface IEntityCreate3Props {
  entity: Partial<IEntity>; // Initiale Daten
  isOpen: boolean;
  schemaName: string;
  mutation: DocumentNode;
  createTemplate?: Partial<IEntity>;
  title?: string; // Überschrift
  onSaved?: (message: React.ReactElement, newData: IEntity) => void; // wird mit einer Erfolgs- oder Fehlermeldung aufgerufen
}
export const EntityCreate3 = (props: IEntityCreate3Props) => {
  const newProps = {
    ...props,
    entity: { _rev: 'NEW', designator: 'XX00000', ...props.entity },
  } as IEntityCreate3Props;
  return <EntityCreate3Inner title="Anlegen" {...newProps} />;
};
// if (forDesignator && newEntity[prop] === ) {

const EntityCreate3Inner = (props: IEntityCreate3Props) => {
  // Ruft das Schema ab. Überlicherweise ist das schon im Cache
  const { loading, error, schema } = useSchema(props.schemaName);
  const entity = { _rev: 'NEW', designator: 'XX00000', ...(props.createTemplate || {}), ...props.entity };

  if (loading || error || !schema) {
    return <LadeChecker loading={loading} error={error} label={`Lade Schema ${props.schemaName} …`} />;
  }
  assertIsObject(props.entity, 'props.entity');
  assertIsObject(schema, 'schema');
  return <_EntityCreateMitSchema3 title="Anlegen" {...props} entity={entity} schema={schema} />;
};

export interface IHdSpinnerProps {
  children: React.ReactNode;
}

 const _EntityCreateMitSchema3 = (
  props: IEntityCreate3Props & {
    schema: JSONSchema7;
  }
) => {
  const [validationMessageBar, setValidationMessageBar] = useState(null);
  const [successMessage, setSuccessMessage] = useState<React.ReactElement>();
  const entity = { ...props.entity, _keyStr: undefined };

  // GraphQL Mutation definieren
  const [state, createEntity] = useMutation(props.mutation);
  const { fetching: saving } = state;
  // Wird aufgerufen, wenn das Panel geschlossen wird
  const saveCallback = useCallback((e) => {
    uebertragen(e);
  }, []);

  // Daten per GraphQL Mutation an den Server senden.
  const uebertragen = async (newEntity) => {
    setValidationMessageBar(null);
    const fInput = { ...(props.createTemplate || {}), ...prepareForGqlPatch({}, newEntity, props.schema) };
    const variables = {
      input: fInput,
      designator: entity.designator,
    };
    console.log('state', state, saving);
    const result = await createEntity(variables);
    const errorMessageBar = checkErrorMessageBar3(result, newEntity, entity);
    if (errorMessageBar) {
      setValidationMessageBar(errorMessageBar);
    } else {
      setSuccessMessage(getSuccessMessageCreate(result?.data?.entity));
    }
  };

  return (
    <div>
      {validationMessageBar}
      {!successMessage ? (
        <div>
          {saving ? <HdSpinner>Speichere…</HdSpinner> : null}
          <div style={saving ? { display: 'none' } : {}}>
            <EntityMutateForm3
              schema={props.schema}
              entity={entity}
              onSave={(params) => saveCallback(params)}
            >
              {validationMessageBar}
            </EntityMutateForm3>
          </div>
        </div>
      ) : (
        successMessage
      )}
    </div>
  );
};

/** Auf das Vorhandensein von Fehlern im GraphQL Layer checken */
function checkErrorMessageBar3(result, entityDiff, entity) {
  let errorMessageBar = null;
  // GraphQL Errors
  if (result?.errors?.map) {
    errorMessageBar = getErrorMessageBar(result);
  } else if (result?.errors) {
    errorMessageBar = getErrorMessageBar(result.errors);
  } else if (result?.error) {
    errorMessageBar = getErrorMessageBar(result.error);
  } else if (result === undefined) {
    errorMessageBar = getErrorMessageBar(
      'Schwieriger Server Fehler - vermutlich ist das Ding nicht erreichbar.'
    );
  }
  // MVCC Errors
  if (result?.data?.patchInfo?.success === false) {
    const oldE = cleanGqlInput(result?.data?.patchInfo?.old);
    const newE = cleanGqlInput({ ...entity, ...entityDiff });

    errorMessageBar = (
      <Alert severity="warning">
        <AlertTitle>Es ist ein Konflikt beim Server-Zugriff auftgetreten</AlertTitle>

        <div>Jemand anders hat die Daten verändert, während Sie daran gearbeitet haben.</div>
        <div>
          Die paralellen Änderungen erfolgten wohl durch{' '}
          <DisplayUserInfo props={result.data.patchInfo.audit[0] as any} />
          um {result.data.patchInfo.audit[0].created_at}.
        </div>
        <div>
          <ReactDiffViewer
            leftTitle="Server"
            oldValue={JSON.stringify(cleanGqlInput(oldE), null, 2)}
            rightTitle="Ihre Daten"
            newValue={JSON.stringify(cleanGqlInput(newE), null, 2)}
            splitView
            hideLineNumbers
            extraLinesSurroundingDiff={1}
            useDarkTheme={false}
            disableWordDiff
          />
        </div>
        <div>
          <em>Ihre</em> Änderungen wurden <b>nicht</b> gespeichert. Am besten laden sie die Seite neu und
          versuchen es erneut.
        </div>
      </Alert>
    );
  }

  return errorMessageBar;
}

/** Message Bar mit GraphQL-Success
 * */
 function getSuccessMessageCreate(entity: IEntity): React.ReactElement {
  return (
    <Stack sx={{ width: '100%' }} spacing={2}>
      <Alert severity="success">Der Datensatz wurde erfolgreich angelegt.</Alert>

      <ReactJson name="Details" collapsed={false} displayObjectSize={false} src={entity} />
    </Stack>
  );
}
